import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import { MappingContext } from '../Contexts/MappingContext';
import { Add, ChevronDown, MoreVertical, Copy, Delete, Sort, ArrowUp, ArrowDown } from 'Components/DesignSystem/Iconography/Icons';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';
import NoDataComponent from 'Components/Common/NoDataComponent';
import { ArsContext } from 'Pages/GMS/ARS/Contexts/ArsContext';
import CreateCategoryModal from './CreateCategoryModal';
import CreateCategoryTypeModal from './CreateCategoryTypeModal';
import Toggle from './Toggle';
import ReviewDropdown from './ReviewDropdown';
import { AttributeContent } from './AttributeContent';
import { AttributeValueContent } from './AttributeValueContent';
import { validateCategoryToggle } from '../Utils/categoryValidation';
import TooltipOverlay from 'Components/Common/components/Button/TooltipOverlay';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useScrollableTableHeight } from '../hooks/useScrollableTableHeight';

type SortOption = 'reporting' | 'attribute' | 'mapped' | 'considered' | 'reviewed';

const CategoryMappingTable = () => {
  const {
    updateCategory,
    addAttributePairToCategory,
    setCategoryRowActions,
    isEditMode,
    readPermission,
    mappingState,
    copyCategory,
    deleteCategory,
    setCategoryTableVisibleData,
    sortState,
    handleSortChange,
    getCurrentTabData,
    pagination,
    loadMoreData,
    industryId
  } = useContext(MappingContext);

  // Infinite scroll for categories
  const categoryPagination = pagination.categories;
  const sentinelRef = useInfiniteScroll(loadMoreData, categoryPagination.hasMore, categoryPagination.loading);

  // Size the table's scroll region to the remaining viewport so only the table
  // body scrolls; the toolbar + sticky column header stay put.
  const { ref: scrollContainerRef, maxHeight: tableMaxHeight } = useScrollableTableHeight();

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [openMappedDropdowns, setOpenMappedDropdowns] = useState<Set<string>>(new Set());
  const [selectedOverflowMenuId, setSelectedOverflowMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [showCreateCategoryTypeModal, setShowCreateCategoryTypeModal] = useState(false);
  const [newlyCopiedId, setNewlyCopiedId] = useState<string | null>(null);
  const [createCategoryForRowId, setCreateCategoryForRowId] = useState<string | null>(null);
  const { allCategories, allCategoryTypes, fetchCategories, fetchCategoryTypes } = useContext(ArsContext);

  const menuRef = useRef<HTMLDivElement | null>(null);

  const filteredData = getCurrentTabData() as unknown as MappingCategory[];

  // CreateCategoryModal (rendered below) and this table's category-toggle validation both
  // read this cache from ArsContext. Fetch (and re-fetch on industry switch) only while the
  // Category tab is actually mounted, instead of ArsContext auto-fetching on every industry
  // switch app-wide regardless of whether the user is anywhere near this tab.
  useEffect(() => {
    if (industryId) {
      fetchCategoryTypes();
      fetchCategories();
    }
  }, [industryId]);

  // Clear selection when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      setSelectedRows(new Set());
    }
  }, [isEditMode]);

  // Handle click outside and scroll for overflow menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setSelectedOverflowMenuId(null);
      }
    }
    function handleScroll() {
      if (selectedOverflowMenuId !== null) {
        setSelectedOverflowMenuId(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [selectedOverflowMenuId]);

  // No client-side sorting - data comes pre-sorted from API via server-side sorting
  // Just use the filtered data directly
  const processedData = filteredData;

  // Validate processedData and auto-correct invalid toggles
  const validatedData = useMemo(() => {
    return processedData.map(category => {
      const validation = validateCategoryToggle(category, processedData);

      // If validation fails and toggle is on, turn it off
      if (!validation.isValid && category.isConsidered) {
        return {
          ...category,
          isConsidered: false,
        };
      }

      return category;
    });
  }, [processedData]);

  useEffect(() => {
    setCategoryTableVisibleData(validatedData);
  }, [validatedData, setCategoryTableVisibleData]);

  useEffect(() => {
    if (isEditMode) {
      validatedData.forEach(category => {
        if (!category.attribute || category.attribute.length === 0) {
          addAttributePairToCategory(category.id);
        }
      });
    }
    // Intentional shallow deps: only fire when entering edit mode or when the
    // visible row count changes. Listing the full `validatedData` would re-run
    // on every cell edit (and `addAttributePairToCategory` mutates the list,
    // so it would loop); listing `addAttributePairToCategory` would re-run
    // every render since it's a non-memoized context value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, validatedData.length]);

  // Row selection handlers
  const handleSelectRow = (categoryId: string, checked: boolean) => {
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(categoryId);
    } else {
      newSelectedRows.delete(categoryId);
    }
    setSelectedRows(newSelectedRows);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(processedData.map(cat => cat.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Bulk row actions
  const handleBulkReviewUpdate = (reviewStatus: boolean) => {
    selectedRows.forEach(categoryId => {
      updateCategory(categoryId, { isReviewed: reviewStatus });
    });
    setSelectedRows(new Set());
  };

  // Handle toggle change with validation
  const handleToggleChange = (categoryId: string, checked: boolean) => {
    const category = validatedData.find(cat => cat.id === categoryId);
    if (!category) return;

    // If trying to enable, check validation
    if (checked) {
      const validation = validateCategoryToggle(category, validatedData);

      if (!validation.isValid) {
        console.warn('Cannot enable toggle:', validation.reason);
        // Optionally, you could show a toast notification here
        return;
      }
    }

    updateCategory(categoryId, { isConsidered: checked });
  };

  const markAsReviewed = () => handleBulkReviewUpdate(true);
  const markAsReviewPending = () => handleBulkReviewUpdate(false);

  const markAsConsidered = () => {
    selectedRows.forEach(categoryId => {
      const category = validatedData.find(cat => cat.id === categoryId);
      if (category) {
        const validation = validateCategoryToggle(category, validatedData);
        if (validation.isValid) {
          updateCategory(categoryId, { isConsidered: true });
        }
      }
    });
    setSelectedRows(new Set());
  };

  // Provide row actions data to context
  useEffect(() => {
    const rowActionsData: RowActionsData = {
      selectedRows,
      selectedCount: selectedRows.size,
      markAsReviewed,
      markAsReviewPending,
      markAsConsidered,
      hasSelection: selectedRows.size > 0
    };

    setCategoryRowActions(isEditMode && selectedRows.size > 0 ? rowActionsData : null);
    // Intentional shallow deps on selectedRows.size: the three markAs* handlers
    // are non-memoized locals (new identity each render) so listing them would
    // re-fire this effect every render; the Set itself is rebuilt on every
    // selection change so its identity tracks .size for our purposes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows.size, isEditMode, setCategoryRowActions]);

  // Map column names to API field names for server-side sorting
  const getApiFieldName = (column: SortOption): string => {
    switch (column) {
      case 'reporting':
        return 'reporting_category';
      case 'mapped':
        return 'mappedcategory_name';
      case 'considered':
        return 'is_considered';
      case 'reviewed':
        return 'is_reviewed';
      default:
        return 'reporting_category';
    }
  };

  const handleSort = (column: SortOption) => {
    const apiFieldName = getApiFieldName(column);
    handleSortChange(apiFieldName);
  };

  const handleAddAttributePair = (categoryId: string) => {
    addAttributePairToCategory(categoryId);
  };

  const toggleMappedDropdown = (categoryId: string) => {
    const newOpen = new Set(openMappedDropdowns);
    if (newOpen.has(categoryId)) {
      newOpen.delete(categoryId);
    } else {
      newOpen.add(categoryId);
    }
    setOpenMappedDropdowns(newOpen);
  };

  // Overflow menu handlers
  const handleOverflowClick = (event: React.MouseEvent<HTMLDivElement>, categoryId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom - window.scrollY - 10,
      left: rect.left - window.scrollX,
    });

    if (selectedOverflowMenuId === categoryId) {
      setSelectedOverflowMenuId(null);
    } else {
      setSelectedOverflowMenuId(categoryId);
    }
  };

  const handleCopyCategory = (category: MappingCategory) => {
    const newCategoryId = copyCategory(category);
    setNewlyCopiedId(newCategoryId);
    setSelectedOverflowMenuId(null);
  };

  const handleDeleteCategory = (categoryId: string) => {
    deleteCategory(categoryId);
    setSelectedOverflowMenuId(null);
  };

  const isAllSelected = validatedData.length > 0 && validatedData.every(cat => selectedRows.has(cat.id));
  const isIndeterminate = validatedData.some(cat => selectedRows.has(cat.id)) && !isAllSelected;

  const shouldShowCopy = (category: MappingCategory): boolean => {
    if (!category.attribute || category.attribute.length === 0 || !category.mapped_categoryid) return false;
    return category.attribute.some(
      pair => pair.attribute && pair.attribute.trim() !== '' &&
        pair.attributeValue && pair.attributeValue.trim() !== ''
    );
  };

  const shouldShowDelete = (category: MappingCategory): boolean => {
    const sameReportingCategories = validatedData.filter(
      cat => cat.reporting_category === category.reporting_category
    );
    return sameReportingCategories.length > 1;
  };

  const onCloseCategoryCreateModal = async (createdCategory?: any) => {
    await fetchCategories();
    await fetchCategoryTypes();
    // Auto-select newly created category in the row that triggered the create
    if (createdCategory && createCategoryForRowId) {
      updateCategory(createCategoryForRowId, {
        mapped_categoryid: createdCategory.id,
        mappedcategory_name: createdCategory.name,
        mapped_categorytype_id: createdCategory.cattype,
        mapped_categorytype_name: createdCategory.categoryTypeName
      });
    }
    setCreateCategoryForRowId(null);
    setShowCreateCategoryModal(false);
  }

  const [preSelectedCategoryTypeId, setPreSelectedCategoryTypeId] = useState<string | null>(null);

  const onCloseCategoryTypeModal = async (createdTypeId?: string) => {
    await fetchCategories();
    await fetchCategoryTypes();
    if (createdTypeId) {
      setPreSelectedCategoryTypeId(createdTypeId);
    }
    setShowCreateCategoryTypeModal(false);
  }

  const OverflowOption = (icon: React.ReactNode, label: string, onClick: () => void) => (
    <div className="tw-py-1 tw-border" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
      <div data-testid="components-categorymappingtable-div-1"
        onClick={onClick}
        className="tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-ink hover:tw-bg-accent-tint tw-cursor-pointer"
        role="menuitem"
      >
        <div className="tw-flex">
          <div className="tw-mt-1">{icon}</div>
          <span className="tw-ml-1">{label}</span>
        </div>
      </div>
    </div>
  );

  // Mapped Category Dropdown Component
  const MappedCategoryDropdown = ({ category }: { category: MappingCategory }) => {
    const isOpen = openMappedDropdowns.has(category.id);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openUpward: false });

    // Handle dropdown positioning and scroll prevention
    useEffect(() => {
      if (!isOpen || !inputRef.current) return;

      // Calculate position
      const rect = inputRef.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

      setDropdownPosition({
        top: shouldOpenUpward ? rect.top + window.scrollY - dropdownHeight : rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        openUpward: shouldOpenUpward
      });

      // Close dropdown on external scroll
      const handleScroll = (e: Event) => {
        if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
          return;
        }
        setOpenMappedDropdowns(new Set());
        setSearchTerm('');
      };

      // Prevent scrolling
      const originalBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      const tableContainer = document.querySelector('.table-container') as HTMLElement;
      const originalTableOverflow = tableContainer?.style.overflow || '';

      if (tableContainer) {
        tableContainer.style.overflow = 'hidden';
        tableContainer.addEventListener('scroll', handleScroll);
      }

      window.addEventListener('scroll', handleScroll, true);

      return () => {
        document.body.style.overflow = originalBodyOverflow;
        if (tableContainer) {
          tableContainer.style.overflow = originalTableOverflow;
          tableContainer.removeEventListener('scroll', handleScroll);
        }
        window.removeEventListener('scroll', handleScroll, true);
      };
    }, [isOpen]);

    // Handle outside click
    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setOpenMappedDropdowns(new Set());
          setSearchTerm('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Group categories by type
    const categoriesByType = useMemo(() => {
      if (!allCategories || !allCategoryTypes) return {};

      const grouped: Record<string, typeof allCategories> = {};

      allCategories.forEach(cat => {
        const categoryType = allCategoryTypes.find(type => type.id === cat.cattype);
        const typeName = categoryType?.name || 'Uncategorized';

        if (!grouped[typeName]) {
          grouped[typeName] = [];
        }
        grouped[typeName].push(cat);
      });

      return grouped;
      // allCategories / allCategoryTypes are closed-over from the parent's
      // useContext; MappedCategoryDropdown is recreated on every parent
      // render so this useMemo's cache resets anyway. Empty deps avoids the
      // lint warning without changing behavior.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filter based on search term
    const filteredCategoriesByType = useMemo(() => {
      if (!searchTerm) return categoriesByType;

      const filtered: Record<string, typeof allCategories> = {};

      Object.entries(categoriesByType).forEach(([typeName, cats]) => {
        const filteredCats = cats.filter(cat =>
          cat.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filteredCats.length > 0) {
          filtered[typeName] = filteredCats;
        }
      });

      return filtered;
    }, [categoriesByType, searchTerm]);

    const handleSelect = (selectedCategory: any, categoryTypeName: string) => {
      updateCategory(category.id, {
        mapped_categoryid: selectedCategory.id,
        mappedcategory_name: selectedCategory.name,
        mapped_categorytype_id: selectedCategory.cattype,
        mapped_categorytype_name: categoryTypeName
      });
      setOpenMappedDropdowns(new Set());
      setSearchTerm('');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    };

    const handleInputFocus = () => {
      if (!isOpen) {
        setSearchTerm('');
        setOpenMappedDropdowns(new Set([category.id]));
      }
    };

    // Auto-focus and select input text when dropdown opens
    useEffect(() => {
      if (isOpen && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isOpen]);

    if (!isEditMode) {
      return (
        <div className="tw-flex tw-items-center tw-mr-4 tw-min-w-0">
          <span className="tw-text-body-primary tw-text-ink tw-truncate tw-min-w-0" title={category.mappedcategory_name || ''}>
            {category.mappedcategory_name || '--'}
          </span>
        </div>
      );
    }

    return (
      <div className="tw-relative tw-w-full" ref={dropdownRef}>
        <div className="tw-relative">
          <input
            ref={inputRef}
            type="text"
            value={isOpen ? searchTerm : (category.mappedcategory_name || '')}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={category?.mappedcategory_name != null ? category?.mappedcategory_name : "Select category"}
            className="tw-w-full tw-py-1 tw-px-3 tw-pr-8 tw-border tw-border-edge-strong tw-rounded tw-bg-surface tw-text-sm tw-text-ink placeholder:tw-text-ink-secondary tw-outline-none focus:tw-ring-1 focus:tw-ring-accent"
            data-testid={`mapped-category-input-${category.id}`}
          />
          <button aria-label="Expand" data-testid="components-categorymappingtable-button-1"
            onClick={() => toggleMappedDropdown(category.id)}
            className="tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-p-1 tw-text-ink"
            type="button"
          >
            <ChevronDown size={16} />
          </button>
        </div>

        {isOpen && (
          <div
            className="tw-fixed tw-bg-surface tw-border tw-border-edge tw-rounded tw-shadow-lg tw-z-[9999] tw-max-h-60 tw-overflow-y-auto no-scrollbar"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: '288px'
            }}
          >
            <div className="tw-py-1 tw-my-1">
              <button data-testid="components-categorymappingtable-button-2"
                onClick={() => {
                  setOpenMappedDropdowns(new Set());
                  setCreateCategoryForRowId(category.id);
                  setShowCreateCategoryModal(true);
                }}
                className="tw-px-2.5 tw-ms-2.5 tw-mb-2 tw-py-[5px] tw-rounded tw-text-accent tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-accent tw-inline-flex tw-justify-center tw-items-center tw-gap-1 hover:tw-bg-accent-tint tw-transition-colors"
                type="button"
              >
                <Add />
                <span className="tw-text-xs tw-font-medium tw-leading-none">
                  Create
                </span>
              </button>

              {Object.keys(filteredCategoriesByType).length === 0 && searchTerm ? (
                <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-ink-tertiary tw-italic">
                  No categories found
                </div>
              ) : (
                Object.entries(filteredCategoriesByType).map(([typeName, categoryList]) => (
                  <div key={typeName}>
                    <div className="tw-px-3 tw-py-1 tw-text-ink-tertiary tw-text-xs tw-uppercase tw-leading-none tw-bg-surface-sunken">
                      {typeName}
                    </div>
                    {categoryList.map((cat) => (
                      <div role="button"
                        key={cat.id}
                        className={`tw-px-3 tw-py-2 tw-text-ink tw-text-sm tw-leading-tight tw-cursor-pointer hover:tw-bg-surface-hover ${category.mapped_categoryid === cat.id ? "tw-bg-accent-tint" : ""}`}
                        onClick={() => handleSelect(cat, typeName)}
                        data-testid={`mapped-category-option-${cat.id}`}
                      >
                        {cat.name}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Overflow Menu Component
  const OverflowMenu = ({ category }: { category: MappingCategory }) => {
    const isMenuOpen = selectedOverflowMenuId === category.id;
    const showCopy = shouldShowCopy(category);
    const showDelete = shouldShowDelete(category);

    if (!showCopy && !showDelete) return null;

    return (
      <div className="tw-relative">
        <div aria-label="More options" role="button"
          className="tw-cursor-pointer tw-p-1 tw-text-ink-secondary hover:tw-bg-surface-hover tw-transition-colors tw-justify-end"
          onClick={(e) => handleOverflowClick(e, category.id)}
          data-testid={`overflow-menu-trigger-${category.id}`}
        >
          <MoreVertical size={16} />
        </div>

        {isMenuOpen && (
          <div
            className="tw-fixed tw-rounded-[4px] tw-z-50 tw-bg-surface tw-ring-1 tw-ring-edge"
            style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left + 40}px` }}
          >
            <div
              ref={menuRef}
              className="tw-origin-top-left tw-mr-8 tw-right-0 tw-absolute tw-mx-3 tw-rounded-[4px] tw-z-50 tw-bg-surface tw-ring-1 tw-ring-edge"
            >
              {showCopy && OverflowOption(
                <Copy />,
                "Copy",
                () => handleCopyCategory(category)
              )}
              {showDelete && OverflowOption(
                <Delete />,
                "Delete",
                () => handleDeleteCategory(category.id)
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // The DS TableHeader sort slot: Sort (unsorted) / ArrowUp / ArrowDown in a
  // 20px ink-tertiary box, replacing the FontAwesome fa-sort glyphs.
  const sortElement = (column: SortOption) => {
    const apiFieldName = getApiFieldName(column);
    const currentSort = sortState.categories;
    const isActive = currentSort.sortBy === apiFieldName;

    return (
      <span
        aria-hidden="true"
        className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-flex-shrink-0 tw-text-ink-tertiary"
      >
        {isActive ? (currentSort.sortOrder === 'asc' ? <ArrowUp /> : <ArrowDown />) : <Sort />}
      </span>
    );
  };

  if (mappingState.loading) {
    return <div><TableSkeletonElement /></div>;
  }

  if (!readPermission) {
    return <NoDataComponent title='Permission Denied!' message="You don't have enough permission to access this feature." />;
  }

  if (!filteredData.length) {
    return <NoDataComponent title="No Data Found" message="No mapping data is available." />;
  }

  return (
    <>
      <div
        className="tw-border tw-border-solid tw-rounded-md tw-mt-3 tw-border-edge tw-overflow-visible tw-mb-4"
        style={{ minHeight: '400px' }}
      >
        <div
          ref={scrollContainerRef}
          className="table-container"
          style={{ width: '100%', maxHeight: tableMaxHeight, overflow: 'auto' }}
        >
          <table className="tw-bg-tone-neutral-tint tw-w-full tw-table-fixed">
            <thead className="tw-sticky tw-top-0 tw-z-20 tw-bg-tone-neutral-tint">
              <tr>
                {/* § 324 — the narrow columns are fixed px, not percentages. This
                    one has to hold 40px (a 16px DS Checkbox at `sm` plus the cell's own
                    24px of `tw-px-3`), and 3% of a `table-fixed` table only clears that
                    once the table is ~1333px wide; below it the box overflowed and sat
                    on the table's border. Same fix as `BaseMappingTable`, which this
                    table does NOT share — it carries its own markup.

                    The comment sits OUT here rather than inside the `&&`: a JSX comment
                    beside the element makes the expression yield two children, which is
                    a syntax error, not a lint nit. */}
                {isEditMode && (
                  <th className="tw-w-12 tw-text-center tw-px-3 tw-py-2">
                    <Checkbox
                      size="sm"
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={(checked) => handleSelectAll(checked)}
                      aria-label="Select all categories"
                      className="tw-justify-center"
                      data-testid="category-select-all-checkbox"
                    />
                  </th>
                )}
                <th className="tw-w-10 tw-text-center tw-text-title-header tw-text-ink-secondary tw-px-3 tw-py-2">#</th>
                <th
                  className="tw-w-[17%] tw-cursor-pointer tw-px-3 tw-py-2 tw-text-left tw-text-title-header tw-text-ink-secondary"
                  onClick={() => handleSort('reporting')}
                >
                  <div className="tw-flex tw-items-center tw-gap-1">
                    <span>Reporting Category</span>
                    {sortElement('reporting')}
                  </div>
                </th>
                <th className="tw-w-[16%] tw-px-3 tw-py-2 tw-text-left tw-text-title-header tw-text-ink-secondary">
                  Attribute
                </th>
                <th className="tw-w-[16%] tw-px-3 tw-py-2 tw-text-left tw-text-title-header tw-text-ink-secondary">
                  Attribute Value
                </th>
                <th
                  className="tw-w-[17%] tw-cursor-pointer tw-px-3 tw-py-2 tw-text-left tw-text-title-header tw-text-ink-secondary"
                  onClick={() => handleSort('mapped')}
                >
                  <div className="tw-flex tw-items-center tw-gap-1">
                    <span>Mapped Category</span>
                    {sortElement('mapped')}
                  </div>
                </th>
                <th
                  className="tw-w-[11%] tw-cursor-pointer tw-px-3 tw-py-2 tw-text-left tw-text-title-header tw-text-ink-secondary"
                  onClick={() => handleSort('considered')}
                >
                  <div className="tw-flex tw-items-center tw-gap-1">
                    <span>Consider</span>
                    {sortElement('considered')}
                  </div>
                </th>
                <th
                  className="tw-w-[14%] tw-cursor-pointer tw-px-3 tw-py-2 tw-text-left tw-text-title-header tw-text-ink-secondary"
                  onClick={() => handleSort('reviewed')}
                >
                  <div className="tw-flex tw-items-center tw-gap-1">
                    <span>Review</span>
                    {sortElement('reviewed')}
                  </div>
                </th>
                {/* The row-overflow column: a 16px `MoreVertical` in a `tw-p-1`
                    trigger inside a `tw-px-1` cell — 32px of content, so it gets
                    pixels for the same reason the two columns above do. */}
                <th className="tw-w-10 tw-text-center tw-px-3 tw-py-2 tw-text-title-header tw-text-ink-secondary">
                </th>
              </tr>
            </thead>
            <tbody>
              {validatedData.map((category, index) => {
                const validation = validateCategoryToggle(category, validatedData);

                return (
                  <tr key={category.id} className="hover:tw-bg-surface-hover">
                    {isEditMode && (
                      <td className={`tw-py-2 tw-px-3 tw-align-top tw-text-center ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                        <Checkbox
                          size="sm"
                          checked={selectedRows.has(category.id)}
                          onChange={(checked) => handleSelectRow(category.id, checked)}
                          aria-label="Select category"
                          className="tw-justify-center tw-mt-1"
                          data-testid={`category-checkbox-${category.id}`}
                        />
                      </td>
                    )}
                    <td className={`tw-py-2 tw-px-3 tw-align-top tw-text-center tw-text-body-primary tw-text-ink ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                      <div className="tw-mt-1">{index + 1}</div>
                    </td>
                    <td className={`tw-py-2 tw-px-3 tw-align-top tw-text-accent tw-text-sm ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                      <div className="tw-mt-1 tw-mr-4 tw-flex tw-items-center tw-gap-2">
                        <span className="tw-truncate tw-min-w-0" title={category.reporting_category}>
                          {category.reporting_category}
                        </span>
                        {newlyCopiedId === category.id && (
                          <div className="tw-w-9 tw-h-4 tw-relative tw-bg-tone-neutral-bold tw-rounded-[20px] tw-flex-shrink-0">
                            <div className="tw-w-6 tw-h-3.5 tw-left-[7px] tw-top-[2px] tw-absolute tw-justify-start tw-text-ink-inverse tw-text-[10px] tw-font-medium tw-leading-none">Copy</div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className={`tw-py-2 tw-px-3 tw-align-top tw-relative ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                      <div className="tw-space-y-1">
                        {category.attribute && category.attribute.length > 0 && (
                          category.attribute.map((pair, pairIndex) => (
                            <div className='tw-pb-3.5' key={`${pairIndex}-${pair.attribute}`}>
                              <AttributeContent
                                key={pairIndex}
                                category={category}
                                pair={pair}
                                pairIndex={pairIndex}
                                index={index}
                              />
                            </div>
                          )))}

                        {isEditMode && (
                          <button
                            onClick={() => handleAddAttributePair(category.id)}
                            className="tw-inline-flex tw-items-center tw-gap-1 hover:tw-text-accent-hover tw-mt-2 tw-text-accent tw-text-xs tw-font-semibold"
                            data-testid={`add-pair-${category.id}`}
                            type="button"
                          >
                            <Add />
                            New Attribute
                          </button>
                        )}
                      </div>
                    </td>
                    <td className={`tw-py-2 tw-px-3 tw-align-top ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                      <div className="tw-space-y-1 tw-mr-4">
                        {category.attribute && category.attribute.length > 0 && (
                          category.attribute.map((pair, pairIndex) => (
                            <div className='tw-pb-3.5' key={`${pairIndex}-${pair.attributeValue}`}>
                              <AttributeValueContent
                                key={pairIndex}
                                category={category}
                                pair={pair}
                                pairIndex={pairIndex}
                              />
                            </div>
                          ))
                        )}
                      </div>
                    </td>
                    <td className={`tw-py-2 tw-px-3 tw-align-top tw-relative tw-overflow-visible ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                      <MappedCategoryDropdown category={category} />
                    </td>
                    <td className={`tw-py-2 tw-px-3 tw-align-top tw-text-start ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                      <div className="tw-mt-1">
                        {validation.isDisabled && validation.reason ? (
                          <TooltipOverlay message={validation.reason}>
                            <Toggle
                              name={`category-toggle-${category.id}`}
                              value={category.isConsidered}
                              onChange={(checked) => handleToggleChange(category.id, checked)}
                              disabled={!isEditMode || validation.isDisabled}
                            />
                          </TooltipOverlay>
                        ) : (
                          <Toggle
                            name={`category-toggle-${category.id}`}
                            value={category.isConsidered}
                            onChange={(checked) => handleToggleChange(category.id, checked)}
                            disabled={!isEditMode || validation.isDisabled}
                          />
                        )}
                      </div>
                    </td>
                    <td className={`tw-py-2 tw-px-3 tw-align-top ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                      <div className="tw-mt-1">
                        <ReviewDropdown
                          value={category.isReviewed}
                          onChange={(value) => updateCategory(category.id, { isReviewed: value })}
                          disabled={!isEditMode || category.isReviewed || !category.mapped_categoryid || validation.isDisabled}
                          itemId={category.id}
                        />
                      </div>
                    </td>
                    <td className={`tw-py-2 tw-px-1 tw-align-top tw-text-center ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                      <div className="tw-mt-1 tw-flex tw-justify-center">
                        {isEditMode && <OverflowMenu category={category} />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Loading indicator lives INSIDE the scroll container so it's
              visible at the bottom of the scrolled list (the container is
              height-capped, so anything below it would be off-screen). */}
          {categoryPagination.loading && (
            <div className="tw-flex tw-justify-center tw-items-center tw-p-4 tw-gap-2">
              <Spinner size="sm" label="Loading more records" />
              <span className="tw-text-sm tw-text-ink-tertiary">Loading more records...</span>
            </div>
          )}

          {/* Sentinel must live INSIDE the scroll container so the
              IntersectionObserver fires as the user scrolls the table body. */}
          <div ref={sentinelRef} style={{ height: '20px', width: '100%' }} data-testid="category-infinite-scroll-sentinel" />
        </div>
      </div>
      <CreateCategoryModal
        isOpen={showCreateCategoryModal}
        onClose={onCloseCategoryCreateModal}
        setShowCreateCategoryTypeModal={setShowCreateCategoryTypeModal}
        preSelectedCategoryTypeId={preSelectedCategoryTypeId}
      />
      <CreateCategoryTypeModal
        isOpen={showCreateCategoryTypeModal}
        onClose={onCloseCategoryTypeModal}
      />
    </>
  );
};

export default CategoryMappingTable;